import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Loader2, Mail, Phone, Tag } from 'lucide-react';
import { uc } from '../unifiedContactsApi';
import { OwnerChip, useStaff } from '../kit/OwnerChip';
import { formatPhone } from '../shared/phone';

const values = (list) => (list || []).map((x) => x?.value).filter(Boolean);

function Field({ label, children, testId }) {
  return (
    <div className="px-4 py-2.5 border-b border-slate-100 last:border-b-0" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">{label}</div>
      {children}
    </div>
  );
}

// Ticket 195: the Inbox's right-hand contact pane, read-only and shaped like
// GoHighLevel's — name, phone, email, tags, owner, and a link to the full
// contact. `row` (the list row) and `fallback` (the loaded conversation's
// contact_snapshot) fill in until the contact itself arrives, and stay if it
// never does.
export default function ContactPane({ contactId, row, fallback, name, className = '' }) {
  const [contact, setContact] = useState(null);
  const [failed, setFailed] = useState(false);
  const staff = useStaff();

  useEffect(() => {
    let alive = true;
    setContact(null);
    setFailed(false);
    uc.get(contactId).then((c) => { if (alive) setContact(c); }).catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [contactId]);

  const phones = values(contact?.phones);
  const emails = values(contact?.emails);
  if (!contact) {
    const p = row?.phone || fallback?.phones?.[0]?.value;
    const e = row?.email || fallback?.emails?.[0]?.value;
    if (p) phones.push(p);
    if (e) emails.push(e);
  }
  const ownerId = contact?.owner_id || row?.owner_id || null;
  const tags = contact?.tags || [];
  const loading = !contact && !failed;

  return (
    <aside className={`flex-col bg-white overflow-y-auto ${className}`} data-testid="contact-pane" aria-label="Contact details">
      <div className="px-4 py-3 border-b border-slate-100">
        <div className="text-sm font-semibold truncate" data-testid="contact-pane-name">{name}</div>
        {loading && <Loader2 className="animate-spin text-slate-400 mt-1" size={12} data-testid="contact-pane-loading" />}
        {failed && <div className="text-[11px] text-slate-400 mt-1" data-testid="contact-pane-failed">Some details could not be loaded.</div>}
      </div>
      <Field label="Phone" testId="contact-pane-phone">
        {phones.length === 0 ? <span className="text-xs text-slate-400">None</span> : phones.map((p) => (
          <div key={p} className="text-sm flex items-center gap-1.5"><Phone size={12} className="text-slate-400" />{formatPhone(p)}</div>
        ))}
      </Field>
      <Field label="Email" testId="contact-pane-email">
        {emails.length === 0 ? <span className="text-xs text-slate-400">None</span> : emails.map((e) => (
          <div key={e} className="text-sm flex items-center gap-1.5 min-w-0"><Mail size={12} className="text-slate-400 flex-shrink-0" /><span className="truncate">{e}</span></div>
        ))}
      </Field>
      <Field label="Tags" testId="contact-pane-tags">
        {tags.length === 0 ? (
          <span className="text-xs text-slate-400">{loading ? '' : 'No tags'}</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {tags.map((t) => (
              <span key={t} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                <Tag size={9} />{t}
              </span>
            ))}
          </div>
        )}
      </Field>
      <Field label="Owner" testId="contact-pane-owner">
        {ownerId ? <OwnerChip userId={ownerId} staff={staff} showName testId="contact-pane-owner-chip" /> : <span className="text-xs text-slate-400">Unassigned</span>}
      </Field>
      <div className="px-4 py-3 space-y-2">
        <Link to={`/portal/contacts/${contactId}`} className="text-sm text-[#070B5F] hover:underline flex items-center gap-1" data-testid="contact-pane-open">
          Open full contact <ExternalLink size={12} />
        </Link>
        {contact?.ghl_edit_url && (
          <a href={contact.ghl_edit_url} target="_blank" rel="noopener noreferrer" className="text-xs text-slate-500 hover:underline flex items-center gap-1" data-testid="contact-pane-ghl">
            View in GoHighLevel <ExternalLink size={11} />
          </a>
        )}
      </div>
    </aside>
  );
}
